import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { ComplianceState, SuspensionReason } from '../types/compliance';

/**
 * Client compliance actions (manager / super_admin) — the staff side of the
 * freeze / suspension / risk-band controls that the AML monitoring and sanctions
 * queues hand off to. All under /manager/clients/{id}.
 */
/** A file answered by an audited export: the bytes and the name the server gave it. */
export interface ExportedFile {
  blob: Blob;
  filename: string;
}

export interface DeathNoticeInput {
  reporterDescription?: string;
  notes?: string;
}

/** Pulls `filename="…"` out of a Content-Disposition header; the caller's fallback otherwise. */
export function filenameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== 'string') return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ? match[1] : fallback;
}

/**
 * A blob request reports its error body as a Blob too, which hides the API's `detail`
 * from `getApiErrorMessage`. Reads it back as JSON so the refusal reason reaches the screen.
 */
async function unwrapBlobError(err: unknown): Promise<unknown> {
  const data: unknown = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      (err as { response: { data: unknown } }).response.data = JSON.parse(await data.text());
    } catch {
      // Not JSON: leave it and let the caller's fallback sentence speak.
    }
  }
  return err;
}

class ComplianceService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async getState(clientId: string): Promise<ComplianceState> {
    const res = await this.api.get<ComplianceState>(
      `/api/v1/manager/clients/${clientId}/compliance-state`,
    );
    return res.data;
  }

  // ── Freeze (money-movement approval block) — single-actor ──
  async freeze(clientId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/manager/clients/${clientId}/freeze`, { reason });
  }

  async unfreeze(clientId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/manager/clients/${clientId}/unfreeze`, { reason });
  }

  // ── Suspension (full read-only hold). Suspend single-actor; reactivate 4-eyes ──
  async suspend(clientId: string, reason: SuspensionReason, note?: string): Promise<void> {
    await this.api.post(`/api/v1/manager/clients/${clientId}/suspension`, { reason, note });
  }

  async proposeReactivation(clientId: string, note: string): Promise<void> {
    await this.api.post(
      `/api/v1/manager/clients/${clientId}/suspension/reactivation-proposal`,
      { note },
    );
  }

  async approveReactivation(clientId: string, note: string): Promise<void> {
    await this.api.post(
      `/api/v1/manager/clients/${clientId}/suspension/reactivation-approval`,
      { note },
    );
  }

  // ── Risk band ──
  /** Manual reassessment. A downgrade returns `pending_second_approval` (four-eyes). */
  async reassess(
    clientId: string, notes?: string,
  ): Promise<{ status: 'applied' | 'pending_second_approval'; to_band?: string }> {
    const res = await this.api.post<{
      status: 'applied' | 'pending_second_approval';
      to_band?: string;
    }>(`/api/v1/manager/clients/${clientId}/risk/reassess`, { notes });
    return res.data;
  }

  async approveDowngrade(clientId: string, requestId: string, reason: string): Promise<void> {
    await this.api.post(
      `/api/v1/manager/clients/${clientId}/risk/downgrade-requests/${requestId}/approve`,
      { reason },
    );
  }

  async rejectDowngrade(clientId: string, requestId: string, reason: string): Promise<void> {
    await this.api.post(
      `/api/v1/manager/clients/${clientId}/risk/downgrade-requests/${requestId}/reject`,
      { reason },
    );
  }

  // ── Death notice (succession D1): recording freezes the account; release is four-eyes ──
  async recordDeathNotice(clientId: string, input: DeathNoticeInput): Promise<void> {
    await this.api.post(`/api/v1/manager/clients/${clientId}/death-notice`, {
      reporter_description: input.reporterDescription || null,
      notes: input.notes || null,
    });
  }

  async releaseDeathNotice(clientId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/manager/clients/${clientId}/death-notice/release`, { reason });
  }

  // ── Audited exports of personal data. Fetched through the authenticated instance as a
  //    blob: nothing sensitive ever sits in a URL, and the download is built in the browser. ──
  /** Regulator-request expediente (super_admin). */
  async exportKycFile(
    clientId: string, reason: string, includeDocuments: boolean,
  ): Promise<ExportedFile> {
    return this.downloadZip(
      `/api/v1/compliance/clients/${clientId}/kyc-file-export`,
      { reason, include_documents: includeDocuments },
      `kyc-file-${clientId}.zip`,
    );
  }

  /** Answer to a data-subject access request (manager, super_admin). */
  async exportPersonalData(
    clientId: string, requestReference: string, includeDocuments: boolean,
  ): Promise<ExportedFile> {
    return this.downloadZip(
      `/api/v1/compliance/clients/${clientId}/personal-data-export`,
      { request_reference: requestReference, include_documents: includeDocuments },
      `personal-data-${clientId}.zip`,
    );
  }

  private async downloadZip(
    path: string, body: object, fallbackName: string,
  ): Promise<ExportedFile> {
    try {
      const res = await this.api.post<Blob>(path, body, { responseType: 'blob' });
      return {
        blob: res.data,
        filename: filenameFromDisposition(res.headers?.['content-disposition'], fallbackName),
      };
    } catch (e: unknown) {
      throw await unwrapBlobError(e);
    }
  }
}

export default new ComplianceService();
